import React, { useCallback, useState } from 'react';
import { Button } from '@/components/ui/button';
import { MarkdownWYSIWYGEditor } from '@/components/common/MarkdownWYSIWYGEditor';

export interface NoteEditorProps {
  initialValue?: string;
  onSave: (content: string) => Promise<void> | void;
  onCancel: () => void;
  isSubmitting?: boolean;
  placeholder?: string;
  height?: number;
}

/**
 * @deprecated This component is now a thin wrapper around MarkdownWYSIWYGEditor.
 * Please use MarkdownWYSIWYGEditor directly for new code.
 */
export function NoteEditor({
  initialValue = '',
  onSave,
  onCancel,
  isSubmitting = false,
  placeholder = 'Write your note here...',
  height = 200,
}: NoteEditorProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [content, setContent] = useState(initialValue);

  const handleSave = useCallback(async () => {
    if (!content.trim()) {
      return;
    }

    setIsSaving(true);
    try {
      await onSave(content.trim());
      // Don't reset content here - let the parent component handle it
    } catch (error) {
      console.error('Failed to save content:', error);
      // Could add toast notification here
    } finally {
      setIsSaving(false);
    }
  }, [content, onSave]);

  const handleCancel = useCallback(() => {
    setContent(initialValue);
    onCancel();
  }, [initialValue, onCancel]);

  const canSave =
    content.trim() !== '' && content.trim() !== initialValue.trim();

  const isLoading = isSaving || isSubmitting;
  return (
    <div className="space-y-3">
      <MarkdownWYSIWYGEditor
        initialValue={initialValue}
        onChange={setContent}
        readonly={isSubmitting}
        placeholder={placeholder}
        height={height}
        showToolbar
      />

      <div className="flex items-center justify-end gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCancel}
          disabled={isLoading}
          className="text-gray-600 hover:text-gray-900"
        >
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={!canSave || isLoading}
          className="min-w-20"
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
